import { useMsal } from '@azure/msal-react'
import { SignOut, User } from '@/components/icons'
import { useMemo } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { markLocallySignedOut } from '@/app/auth/local-logout'
import { logoutShield } from '@/app/auth/shield-token-source'
import { HAS_DEV_JWT, USING_SHIELD_AUTH } from '@/lib/env'

/**
 * Avatar + dropdown for the signed-in operator. Reads name/email off the
 * active MSAL account. Sign-out clears Pulse's MSAL cache only — never
 * Microsoft's end-session — so Outlook / Teams stay signed in.
 */
export function UserMenu() {
  const { instance, accounts } = useMsal()
  const account = accounts[0]
  const { initials, name, email } = useMemo(() => extractIdentity(account), [account])

  const onSignOut = () => {
    if (HAS_DEV_JWT) return
    if (USING_SHIELD_AUTH) {
      markLocallySignedOut()
      void logoutShield()
      return
    }
    markLocallySignedOut()
    void instance.logoutRedirect({
      account: instance.getActiveAccount() ?? account,
      onRedirectNavigate: () => false,
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex size-9 max-md:size-11 items-center justify-center rounded-full bg-[color:var(--muted)] text-[11.5px] font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--input)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]"
        aria-label={`Account menu for ${name || 'operator'}`}
      >
        {initials || <User className="size-4" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <div className="px-2 py-1.5 text-[12.5px]">
          <div className="font-semibold text-[color:var(--foreground)]">{name || 'Operator'}</div>
          {email && <div className="text-[color:var(--muted-foreground)]">{email}</div>}
          {HAS_DEV_JWT && (
            <div className="mt-1.5 inline-flex rounded-md bg-[color:var(--warn-soft)] px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--warn)]">
              Dev JWT override
            </div>
          )}
          {USING_SHIELD_AUTH && (
            <div className="mt-1.5 inline-flex rounded-md bg-[color:var(--primary-soft)] px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--primary)]">
              Shield session
            </div>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut} disabled={HAS_DEV_JWT}>
          <SignOut className="size-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function extractIdentity(account?: {
  name?: string | null
  username?: string
  idTokenClaims?: unknown
}) {
  const name = account?.name ?? account?.username ?? ''
  const email = account?.username ?? ''
  const initials = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')
  return { name, email, initials }
}
